import React, { useState, useEffect, useRef } from 'react';
import FightList from './FightList';
import FighterComparison from './FighterComparison';
import FighterSheet from './FighterSheet';
import IntroAnimation from './IntroAnimation';
import { useFightData } from '../contexts/FightDataContext';
import { useIntro } from '../App';

const Home: React.FC = () => {
  const { selectedFightIndex, setSelectedFightIndex } = useFightData();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { showingIntro, setShowingIntro } = useIntro();
  const isInitialSelection = useRef(true);
  const lastSelectedIndex = useRef<number | null>(null);

  // Handle intro completion
  const handleIntroComplete = () => {
    localStorage.setItem('hasSeenIntro', 'true');
    setShowingIntro(false);
  };

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint in Tailwind
    };
    
    // Check on initial render
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Handle fighter selection and sheet opening
  const handleFighterSelect = (index: number) => {
    // Always update the selected index
    setSelectedFightIndex(index);
    
    // On mobile, open the sheet if this is a user-triggered selection
    if (isMobile && !isInitialSelection.current) {
      setIsSheetOpen(true);
    }
    
    // Track the last selected index
    lastSelectedIndex.current = index;
  };

  // Handle initial data load
  useEffect(() => {
    if (isInitialSelection.current) {
      // On desktop, we want to show the first fighter by default
      if (!isMobile) {
        setSelectedFightIndex(0);
      }
      isInitialSelection.current = false;
    }
  }, [isMobile, setSelectedFightIndex]);

  // Handle sheet close
  const handleSheetClose = () => {
    setIsSheetOpen(false);
    // On mobile, clear the selected fighter when sheet is closed
    if (isMobile) {
      setSelectedFightIndex(-1);
    }
  };

  if (showingIntro) {
    return <IntroAnimation onComplete={handleIntroComplete} />;
  }

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fade-in {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>

      <div className="flex flex-col md:flex-row w-full max-w-[1800px] mx-auto animate-fade-in">
        <div className={`${isMobile ? 'min-h-[calc(100vh-73px)]' : 'flex flex-1 h-screen overflow-hidden'} pt-[73px]`}>
          <div className={`${isMobile ? '' : 'w-full md:w-auto h-[calc(100vh-73px)] overflow-auto'}`}>
            <FightList onFighterSelect={handleFighterSelect} />
          </div>
          <main className={`${isMobile ? '' : 'flex-1 h-[calc(100vh-73px)] overflow-auto'} bg-white-only dark:bg-dark-background relative hidden md:block`}>
            <div className="max-w-[825px] mx-auto px-4">
              <FighterComparison />
            </div>
          </main>
          
          {/* Mobile fighter sheet modal */}
          {isMobile && (
            <FighterSheet 
              isOpen={isSheetOpen} 
              onClose={handleSheetClose}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Home; 